import { useEffect, useState } from "react";
import api from "../lib/api";
import { Sparkles, Plus, Star, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function HooksBank() {
  const [hooks, setHooks] = useState([]);
  const [filter, setFilter] = useState("all");
  const [aiPrompt, setAiPrompt] = useState({ niche: "", subject: "", platform: "instagram", count: 5 });
  const [loading, setLoading] = useState(false);
  const [aiResult, setAiResult] = useState([]);
  const [manualText, setManualText] = useState("");

  const load = async () => setHooks(await api.listHooks());
  useEffect(() => { load(); }, []);

  const generate = async () => {
    if (!aiPrompt.subject) return toast.error("Précise un sujet");
    setLoading(true); setAiResult([]);
    try {
      const r = await api.aiHooks({ prompt: aiPrompt.subject, niche: aiPrompt.niche, platform: aiPrompt.platform, count: aiPrompt.count });
      const txt = r.raw || "";
      const m = txt.match(/\{[\s\S]*\}/);
      if (m) {
        const parsed = JSON.parse(m[0]);
        setAiResult(parsed.hooks || []);
      } else {
        toast.error("Format IA invalide");
      }
    } catch (e) {
      toast.error("Erreur IA");
    } finally { setLoading(false); }
  };

  const saveAi = async (h) => {
    await api.createHook({ text: h.text, category: h.category, score: h.score || 80, platform: aiPrompt.platform });
    toast.success("Hook ajouté à la banque");
    load();
  };

  const addManual = async () => {
    if (!manualText) return;
    await api.createHook({ text: manualText, category: "general", platform: "instagram", score: 75 });
    setManualText("");
    toast.success("Hook créé");
    load();
  };

  const toggleFav = async (h) => { await api.updateHook(h.id, { favorite: !h.favorite }); load(); };
  const del = async (id) => { await api.deleteHook(id); toast.success("Supprimé"); load(); };

  const filtered = hooks.filter(h => filter === "all" || (filter === "fav" && h.favorite) || h.category === filter);
  const categories = ["all", "fav", "curiosity", "controversy", "value", "story", "transformation", "fear"];

  return (
    <div className="space-y-8 animate-fadeUp" data-testid="hooks-bank-page">
      <header className="border-l-2 border-[#CCFF00] pl-5">
        <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-[#CCFF00] mb-2">// CRÉATION & IA</div>
        <h1 className="font-display text-4xl sm:text-5xl font-bold text-white tracking-tight">Banque de Hooks</h1>
        <p className="text-zinc-400 mt-2">Génère, stocke et catégorise les hooks qui stoppent le scroll en 0-1s.</p>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-5">
        <div className="lg:col-span-2 border border-[#00D4FF]/30 bg-[#00D4FF]/[0.02] rounded-sm p-6 space-y-4" data-testid="ai-generator">
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-[#00D4FF]" strokeWidth={1.5} />
            <h2 className="font-display text-xl font-semibold text-white">Générateur IA</h2>
            <span className="text-[10px] font-mono uppercase tracking-wider text-[#00D4FF] ml-auto">CLAUDE 4.5</span>
          </div>

          <div className="space-y-3">
            <input value={aiPrompt.niche} onChange={e => setAiPrompt({ ...aiPrompt, niche: e.target.value })}
              placeholder="Niche (ex: fitness, fintech...)" data-testid="ai-niche"
              className="w-full bg-[#0C0C0E] border border-[#1E1E24] focus:border-[#00D4FF] rounded-sm px-3 py-2 text-sm text-white outline-none" />
            <textarea value={aiPrompt.subject} onChange={e => setAiPrompt({ ...aiPrompt, subject: e.target.value })}
              placeholder="Sujet / angle (ex: secrets de growth IG)" rows={3} data-testid="ai-subject"
              className="w-full bg-[#0C0C0E] border border-[#1E1E24] focus:border-[#00D4FF] rounded-sm px-3 py-2 text-sm text-white outline-none resize-none" />
            <div className="grid grid-cols-2 gap-3">
              <select value={aiPrompt.platform} onChange={e => setAiPrompt({ ...aiPrompt, platform: e.target.value })}
                className="bg-[#0C0C0E] border border-[#1E1E24] rounded-sm px-3 py-2 text-sm text-white">
                <option value="instagram">Instagram</option><option value="tiktok">TikTok</option>
                <option value="threads">Threads</option><option value="x">X / Twitter</option>
                <option value="youtube">YouTube Shorts</option>
              </select>
              <select value={aiPrompt.count} onChange={e => setAiPrompt({ ...aiPrompt, count: +e.target.value })}
                className="bg-[#0C0C0E] border border-[#1E1E24] rounded-sm px-3 py-2 text-sm text-white">
                {[3, 5, 8, 10].map(n => <option key={n} value={n}>{n} hooks</option>)}
              </select>
            </div>
            <button onClick={generate} disabled={loading} data-testid="generate-hooks-btn"
              className="w-full bg-[#00D4FF]/10 border border-[#00D4FF]/40 text-[#00D4FF] hover:bg-[#00D4FF]/20 px-4 py-2.5 rounded-sm font-mono uppercase tracking-wider text-sm flex items-center justify-center gap-2 transition-all">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
              {loading ? "Génération..." : "Générer"}
            </button>
          </div>

          {aiResult.length > 0 && (
            <div className="space-y-2 mt-4 pt-4 border-t border-[#1E1E24]">
              <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-500">Résultats IA</div>
              {aiResult.map((h, i) => (
                <div key={i} className="border border-[#1E1E24] hover:border-[#00D4FF]/30 bg-[#0C0C0E] rounded-sm p-3 group transition">
                  <div className="text-sm text-white mb-2">{h.text}</div>
                  <div className="flex items-center justify-between text-[10px] font-mono uppercase">
                    <span className="text-[#00D4FF]">{h.category} · {h.score}/100</span>
                    <button onClick={() => saveAi(h)} className="text-zinc-500 hover:text-[#CCFF00] flex items-center gap-1" data-testid={`save-ai-hook-${i}`}>
                      <Plus className="w-3 h-3" /> Sauver
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="lg:col-span-3 space-y-5">
          <div className="flex flex-wrap gap-2">
            {categories.map(c => (
              <button key={c} onClick={() => setFilter(c)} data-testid={`filter-${c}`}
                className={`px-3 py-1.5 rounded-sm text-xs font-mono uppercase tracking-wider transition ${
                  filter === c ? "bg-[#CCFF00] text-black" : "border border-[#1E1E24] text-zinc-400 hover:text-white hover:border-white/30"
                }`}>{c === "all" ? "Tous" : c === "fav" ? "★ Fav" : c}</button>
            ))}
          </div>

          <div className="flex gap-2">
            <input value={manualText} onChange={e => setManualText(e.target.value)}
              placeholder="Ajouter un hook manuellement..." data-testid="manual-hook-input"
              className="flex-1 bg-[#0C0C0E] border border-[#1E1E24] focus:border-[#CCFF00] rounded-sm px-3 py-2 text-sm text-white outline-none" />
            <button onClick={addManual} className="bg-[#CCFF00] text-black px-4 py-2 rounded-sm hover:bg-[#B3E600] text-sm flex items-center gap-1" data-testid="add-manual-hook">
              <Plus className="w-4 h-4" /> Ajouter
            </button>
          </div>

          <div className="space-y-2">
            {filtered.length === 0 ? (
              <div className="border border-dashed border-[#1E1E24] rounded-sm p-8 text-center text-zinc-500 text-sm">
                Aucun hook dans cette catégorie. Génère-en avec l'IA →
              </div>
            ) : filtered.map(h => (
              <div key={h.id} className="border border-[#1E1E24] hover:border-white/20 bg-[#0A0A0C] rounded-sm p-4 flex items-start gap-3" data-testid={`hook-row-${h.id}`}>
                <button onClick={() => toggleFav(h)} className="mt-0.5">
                  <Star className={`w-4 h-4 ${h.favorite ? "text-[#CCFF00] fill-[#CCFF00]" : "text-zinc-600"}`} strokeWidth={1.5} />
                </button>
                <div className="flex-1 min-w-0">
                  <div className="text-white text-sm">{h.text}</div>
                  <div className="flex items-center gap-3 mt-2 text-[10px] font-mono uppercase tracking-wider text-zinc-500">
                    <span className="text-[#CCFF00]">{h.category}</span>
                    <span>·</span>
                    <span>{h.platform}</span>
                    <span>·</span>
                    <span>Score {h.score}</span>
                  </div>
                </div>
                <button onClick={() => del(h.id)} className="text-zinc-600 hover:text-red-500 mt-0.5">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
